import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  allLocalizedStrings,
  exerciseRegion,
  rankSwapCandidates,
  type EquipmentKind as EquipmentKindT,
  type Exercise,
  type MuscleRegion as MuscleRegionT,
  type SwapCandidate,
} from '@jackedlogs/shared';

// The swap picker's search/filter/ranking machine, lifted out of the screen
// (DS-42). Pure state + derivation, no JSX — the same seam `_useMachineChoice`
// already carved on this screen. It left because the screen had to make room:
// declaring the three bands added markup to a file that was 418 lines against
// a 500 hard cap, and the part with no markup in it is the part that moves.

const SUGGEST_LIMIT = 5;

// EQ-1-GYM-COLDSTART R4 — declared-missing exercises go LAST, in the order
// they already had. A stable partition, not a comparator: `Array.sort` on a
// boolean key would be free to reshuffle the equal-ranked ones, and the
// suggested list's order IS the ranking rankSwapCandidates computed.
// ⛔ Partition, never `filter`: nothing may leave the list.
const sinkLast = (
  list: readonly Exercise[],
  sunk: ReadonlySet<string>,
): Exercise[] =>
  sunk.size === 0
    ? [...list]
    : [
        ...list.filter((ex) => !sunk.has(ex.slug)),
        ...list.filter((ex) => sunk.has(ex.slug)),
      ];

const norm = (s: string): string =>
  s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

export type SwapSection = {
  key: 'suggested' | 'all';
  title: string;
  data: Exercise[];
  candidates: Map<string, SwapCandidate>;
};

export function useSwapPickerFilters({
  visible,
  current,
  catalog,
  sunk,
}: {
  visible: boolean;
  current: Exercise | null;
  catalog: Exercise[] | null;
  /** EQ-1-GYM-COLDSTART — exercises the athlete declared they cannot train
   *  here at all. They sink to the bottom of both sections; they are never
   *  removed from either. */
  sunk: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [regions, setRegions] = useState<Set<MuscleRegionT>>(() => new Set());
  const [equipment, setEquipment] = useState<Set<EquipmentKindT>>(
    () => new Set(),
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Reset transient state every time the picker reopens so a stale
  // filter from a previous entry doesn't bleed into the new context.
  useEffect(() => {
    if (visible) {
      setSearch('');
      setRegions(new Set());
      setEquipment(new Set());
      setFiltersOpen(false);
    }
  }, [visible]);

  const toggleRegion = (region: MuscleRegionT) => {
    setRegions((prev) => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  };

  const toggleEquipment = (kind: EquipmentKindT) => {
    setEquipment((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const clearFilters = () => {
    setRegions(new Set());
    setEquipment(new Set());
    setSearch('');
  };

  const hasFilters = regions.size + equipment.size > 0 || search.length > 0;

  const ranked = useMemo<SwapCandidate[]>(() => {
    if (current === null || catalog === null) return [];
    return rankSwapCandidates(current, catalog);
  }, [current, catalog]);

  const candidatesBySlug = useMemo(() => {
    const m = new Map<string, SwapCandidate>();
    for (const c of ranked) m.set(c.exercise.slug, c);
    return m;
  }, [ranked]);

  const passesFilters = (ex: Exercise): boolean => {
    if (regions.size > 0) {
      const region = exerciseRegion(ex);
      if (!region || !regions.has(region)) return false;
    }
    if (equipment.size > 0) {
      if (!ex.equipment || !equipment.has(ex.equipment)) return false;
    }
    const q = norm(search.trim());
    if (q !== '') {
      if (!norm(allLocalizedStrings(ex.name)).includes(q)) {
        const aliases = ex.aliases ?? [];
        if (!aliases.some((a) => norm(a).includes(q))) return false;
      }
    }
    return true;
  };

  const suggestedAll = useMemo<Exercise[]>(
    () => ranked.slice(0, SUGGEST_LIMIT).map((c) => c.exercise),
    [ranked],
  );

  const suggested = useMemo<Exercise[]>(
    () => sinkLast(suggestedAll.filter(passesFilters), sunk),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestedAll, regions, equipment, search, sunk],
  );

  // "All" section excludes the slugs we already promoted to Suggested
  // (independent of the active filter — that's a stable identity
  // partition) and the current entry's own slug. Then filters apply
  // to the remainder so chips behave the same on both sections.
  const allOthers = useMemo<Exercise[]>(() => {
    if (catalog === null || current === null) return [];
    const promoted = new Set(suggestedAll.map((e) => e.slug));
    return sinkLast(
      catalog
        .filter((ex) => ex.slug !== current.slug && !promoted.has(ex.slug))
        .filter(passesFilters)
        .sort((a, b) => a.name.en.localeCompare(b.name.en)),
      sunk,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, current, suggestedAll, regions, equipment, search, sunk]);

  const sections: SwapSection[] = useMemo(() => {
    const out: SwapSection[] = [];
    if (suggested.length > 0) {
      out.push({
        key: 'suggested',
        title: t('swap.suggestedSection', { defaultValue: 'Suggested' }),
        data: suggested,
        candidates: candidatesBySlug,
      });
    }
    out.push({
      key: 'all',
      title: t('swap.allSection', { defaultValue: 'All exercises' }),
      data: allOthers,
      candidates: candidatesBySlug,
    });
    return out;
  }, [suggested, allOthers, candidatesBySlug, t]);

  const reasonChips = (cand: SwapCandidate | undefined): string[] => {
    if (!cand) return [];
    const out: string[] = [];
    if (cand.tagOverlap > 0)
      out.push(t('swap.reasonTags', { count: cand.tagOverlap }));
    if (cand.primaryOverlap > 0)
      out.push(t('swap.reasonPrimary', { count: cand.primaryOverlap }));
    if (!cand.sameEquipment && current?.equipment)
      out.push(t('swap.reasonDifferentEquipment'));
    return out;
  };

  return {
    search,
    setSearch,
    regions,
    equipment,
    filtersOpen,
    setFiltersOpen,
    toggleRegion,
    toggleEquipment,
    clearFilters,
    hasFilters,
    sections,
    reasonChips,
    empty: sections.every((s) => s.data.length === 0),
  };
}
